import { CityType, CountryType, LocationValueType } from "@/interfaces";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

/** Searches countries, states and cities in the tenant-visible location catalogue. */
export const useGetDestinationsQuery = (search: string) => {
  return useQuery({
    queryKey: ["destination", search],
    queryFn: async (): Promise<CountryType[]> => {
      const res = await axios.get(`/api/generics/destination?search=${encodeURIComponent(search)}`);
      return res.data;
    },
    staleTime: Infinity,
    enabled: false,
  });
};

/** Searches country reference data, optionally including travel states/regions. */
export const useGetCountriesQuery = (search: string, includeStates = false) => {
  return useQuery({
    queryKey: ["country", search, includeStates],
    queryFn: async (): Promise<CountryType[]> => {
      const res = await axios.get(`/api/generics/country?search=${encodeURIComponent(search)}&includeStates=${includeStates}`);
      return res.data;
    },
    staleTime: Infinity,
    enabled: false,
  });
};

/** Searches travel destinations across countries and optional states/regions. */
export const useGetDestinationRegionsQuery = (search: string) => useGetCountriesQuery(search, true);

/** Loads cities automatically for the selected country or state/region, including an empty search. */
export const useGetCitiesQuery = (country: string, search: string) => {
  return useQuery({
    queryKey: ["cities", country, search],
    queryFn: async (): Promise<CityType[]> => {
      const res = await axios.get(`/api/generics/country/${encodeURIComponent(country)}/city?search=${encodeURIComponent(search)}`);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(country.trim()),
  });
};

/** Searches cities across the location catalogue and includes their countries. */
export const useGetCityLocationsQuery = (search: string) => {
  return useQuery({
    queryKey: ["city-locations", search],
    queryFn: async (): Promise<CityType[]> => {
      const res = await axios.get(`/api/generics/city?search=${encodeURIComponent(search)}`);
      return res.data;
    },
    staleTime: Infinity,
    enabled: false,
  });
};

/** Searches canonical city and country values from the locations catalogue. */
export const useGetLocationValuesQuery = (search: string) => {
  return useQuery({
    queryKey: ["location-values", search],
    queryFn: async (): Promise<LocationValueType[]> => {
      const res = await axios.get(`/api/generics/location?search=${encodeURIComponent(search)}`);
      return res.data;
    },
    staleTime: Infinity,
    enabled: false,
  });
};

export type TimeZoneValueType = { value: string; locations: string[] };

/** Searches distinct timezones configured on tenant-visible locations. */
export const useGetTimeZoneValuesQuery = (search: string) => useQuery({
  queryKey: ["timezone-values", search],
  queryFn: async (): Promise<TimeZoneValueType[]> => {
    const res = await axios.get(`/api/generics/timezone?search=${encodeURIComponent(search)}`);
    return res.data;
  },
  staleTime: 5 * 60 * 1000,
});

/**
 * Fetches currencies with search functionality from locations dataset.
 * @param search Search term for currency code.
 */
export const useGetCurrenciesQuery = (search: string) => {
  return useQuery({
    queryKey: ["currency", search],
    queryFn: async (): Promise<CountryType[]> => {
      const res = await axios.get(`/api/generics/currency?search=${search}`);
      return res.data;
    },
    staleTime: Infinity,
    enabled: false,
  });
};
